import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/layout/Layout';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { hasPermission, tieneAcceso, primeraPestanaPermitida, APP, TAB, Clave } from './utils/authRoles';

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage';

// Dashboard queda eager: es el destino post-login de casi todas las cuentas, así que no
// tiene sentido diferirlo. El resto de las páginas se carga bajo demanda (más abajo): antes
// todas viajaban en el bundle principal, así que abrir el Dashboard descargaba y parseaba
// también Planificación (5.7k líneas), Acreditación (4.8k) y Visualización (3.5k), entre
// otras, sin que esa cuenta fuera a visitarlas nunca en la sesión.
import { DashboardPage } from './pages/DashboardPage';

// Lazy: cada página pesada se descarga solo cuando se navega a su ruta.
const EstadoDocumentacionPage = lazy(() => import('./pages/EstadoDocumentacionPage').then(mod => ({ default: mod.EstadoDocumentacionPage })));
const CalendarioPage = lazy(() => import('./pages/CalendarioPage'));
const ServiciosPage = lazy(() => import('./pages/ServiciosPage').then(mod => ({ default: mod.ServiciosPage })));
const VisualizacionPage = lazy(() => import('./pages/VisualizacionPage'));
const ConfiguracionPage = lazy(() => import('./pages/ConfiguracionPage').then(mod => ({ default: mod.ConfiguracionPage })));
const ConfiguracionAuditoriaPage = lazy(() => import('./pages/ConfiguracionAuditoriaPage'));
const NotificacionesAdminPage = lazy(() => import('./pages/NotificacionesAdminPage'));
const ResponsablesPage = lazy(() => import('./pages/ResponsablesPage'));
const UsuariosPage = lazy(() => import('./pages/UsuariosPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const CentrosCostoPage = lazy(() => import('./pages/CentrosCostoPage'));
const CargaMasivaDocumentosPage = lazy(() => import('./pages/CargaMasivaDocumentosPage'));
const DocumentosExistentesPage = lazy(() => import('./pages/DocumentosExistentesPage'));
const PlanificacionPage = lazy(() => import('./pages/PlanificacionPage').then(mod => ({ default: mod.PlanificacionPage })));
const PlanificacionHistorialPage = lazy(() => import('./pages/PlanificacionHistorialPage'));
const AcreditacionPage = lazy(() => import('./pages/AcreditacionPage').then(mod => ({ default: mod.AcreditacionPage })));
const ResumenPlanificacionPage = lazy(() => import('./pages/ResumenPlanificacionPage'));
const VerificacionPublicPage = lazy(() => import('./pages/VerificacionPublicPage'));
const CoberturaMatrizPage = lazy(() => import('./pages/CoberturaMatrizPage'));
const TestResponsablesPage = lazy(() => import('./pages/TestResponsablesPage'));
const PersonalPage = lazy(() => import('./pages/PersonalPage').then(mod => ({ default: mod.PersonalPage })));
const MultiUploadPage = lazy(() => import('./pages/MultiUploadPage'));
// Pre-SISI es un módulo standalone (login y sesión propios, sin el Layout/Sidebar
// principal) — ver src/presisi/PreSisiApp.tsx. Se monta como un árbol de rutas aparte,
// NO dentro de ProtectedRoute/Layout como el resto de la plataforma.
const PreSisiApp = lazy(() => import('./presisi/PreSisiApp'));

const PageFallback = <div className="p-8"><LoadingSpinner /></div>;

// Cierra la sesión y vuelve al login, donde queda visible el mensaje de "sin acceso" que
// dejó bloquearAccesoPlataforma. Es un componente aparte porque el efecto tiene que
// correr incondicionalmente, y SisiRoute decide su rama recién después de varios ifs.
const ExpulsarSinAcceso: React.FC<{ alExpulsar: () => void }> = ({ alExpulsar }) => {
  React.useEffect(() => {
    alExpulsar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Navigate to="/login" replace />;
};

// `permiso` es la pestaña que muestra la ruta (ver PESTANAS en utils/authRoles.ts), que
// se configura por rol en /configuracion/roles. Sin ella se rebota a la primera pestaña
// que el usuario sí ve, para que no baste con escribir la URL a mano. Reemplaza a las
// antiguas PrivilegedRoute (accreditation.manage) y PlanificacionRoute
// (programacion.access): ahora son pestañas con esa acción como requisito.
const SisiRoute: React.FC<{ children: React.ReactNode; permiso?: Clave }> = ({ children, permiso }) => {
  const { isAuthenticated, isLoading, user, bloquearAccesoPlataforma } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Sesión del SISI abierta con una cuenta sin acceso al SISI (ej. un rol solo Pre-SISI,
  // o uno al que se le quitó el acceso después de iniciar sesión): se cierra y se avisa
  // por qué, en vez de rebotarla en silencio.
  const destino = primeraPestanaPermitida(user);
  if (!tieneAcceso(user, APP.SISI) || !destino) {
    return <ExpulsarSinAcceso alExpulsar={bloquearAccesoPlataforma} />;
  }

  if (permiso && !hasPermission(user, permiso)) {
    return <Navigate to={destino} replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={primeraPestanaPermitida(user) || '/dashboard'} replace />;
  }

  return <>{children}</>;
};

function App() {
  return (
    <Routes>
      {/* Public Routes - Authentication Required */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      {/* Registration routes disabled: redirect to login */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/register-personal" element={<Navigate to="/login" replace />} />

      {/* Pre-SISI: módulo standalone con su propio login/sesión (ver
          src/presisi/PreSisiApp.tsx). Deliberadamente FUERA de SisiRoute/Layout:
          no comparte sidebar, header ni sesión con el resto de la plataforma. El
          árbol de rutas interno (login / bandeja) lo resuelve el propio módulo. */}
      <Route
        path="/pre-sisi/*"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>}>
            <PreSisiApp />
          </Suspense>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <SisiRoute permiso={TAB.DASHBOARD}>
            <DashboardPage />
          </SisiRoute>
        }
      />

      <Route
        path="/personal"
        element={
          <SisiRoute permiso={TAB.PERSONAL_LISTADO}>
            <Suspense fallback={PageFallback}>
              <PersonalPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/personal/carga-masiva"
        element={
          <SisiRoute permiso={TAB.PERSONAL_CARGA_MASIVA}>
            <Suspense fallback={PageFallback}>
              <CargaMasivaDocumentosPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/documentos/existentes"
        element={
          <SisiRoute permiso={TAB.DOCUMENTOS_EXISTENTES}>
            <Suspense fallback={PageFallback}>
              <DocumentosExistentesPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/personal/estado-documentacion"
        element={
          <SisiRoute permiso={TAB.PERSONAL_ESTADO_DOCUMENTACION}>
            <Suspense fallback={PageFallback}>
              <EstadoDocumentacionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/calendario"
        element={
          <SisiRoute>
            <Suspense fallback={PageFallback}>
              <CalendarioPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/planificacion"
        element={
          <SisiRoute permiso={TAB.PROGRAMACION_PLANIFICACION}>
            <Suspense fallback={PageFallback}>
              <PlanificacionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/planificacion/historial"
        element={
          <SisiRoute permiso={TAB.PROGRAMACION_PLANIFICACION}>
            <Suspense fallback={PageFallback}>
              <PlanificacionHistorialPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/planificacion/resumen"
        element={
          <SisiRoute permiso={TAB.PROGRAMACION_RESUMEN}>
            <Suspense fallback={PageFallback}>
              <ResumenPlanificacionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/servicios"
        element={
          <SisiRoute permiso={TAB.SERVICIOS_SERVICIOS}>
            <Suspense fallback={PageFallback}>
              <ServiciosPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/servicios/visualizacion"
        element={
          <SisiRoute permiso={TAB.SERVICIOS_VISUALIZACION}>
            <Suspense fallback={PageFallback}>
              <VisualizacionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/servicios/cobertura"
        element={
          <SisiRoute permiso={TAB.SERVICIOS_COBERTURA}>
            <Suspense fallback={PageFallback}>
              <CoberturaMatrizPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/acreditacion"
        element={
          <SisiRoute permiso={TAB.SERVICIOS_ACREDITACION}>
            <Suspense fallback={PageFallback}>
              <AcreditacionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/test/responsables"
        element={
          <SisiRoute permiso={'accreditation.manage'}>
            <Suspense fallback={PageFallback}>
              <TestResponsablesPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion"
        element={
          <SisiRoute>
            <Suspense fallback={PageFallback}>
              <ConfiguracionPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/auditoria"
        element={
          <SisiRoute permiso={TAB.CONFIG_AUDITORIA}>
            <Suspense fallback={PageFallback}>
              <ConfiguracionAuditoriaPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/centros-costo"
        element={
          <SisiRoute permiso={TAB.CONFIG_CENTROS_COSTO}>
            <Suspense fallback={PageFallback}>
              <CentrosCostoPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/notificaciones"
        element={
          <SisiRoute permiso={TAB.CONFIG_NOTIFICACIONES}>
            <Suspense fallback={PageFallback}>
              <NotificacionesAdminPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/responsables"
        element={
          <SisiRoute permiso={TAB.CONFIG_RESPONSABLES}>
            <Suspense fallback={PageFallback}>
              <ResponsablesPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/usuarios"
        element={
          <SisiRoute permiso={TAB.CONFIG_USUARIOS}>
            <Suspense fallback={PageFallback}>
              <UsuariosPage />
            </Suspense>
          </SisiRoute>
        }
      />

      <Route
        path="/configuracion/roles"
        element={
          <SisiRoute permiso={TAB.CONFIG_ROLES}>
            <Suspense fallback={PageFallback}>
              <RolesPage />
            </Suspense>
          </SisiRoute>
        }
      />

      {/* Default redirect - always go to login first */}
      <Route path="/" element={<Navigate to="/login" replace />} />

      {/* Public verification page (no auth required) */}
      <Route path="/verificacion" element={<Suspense fallback={PageFallback}><VerificacionPublicPage /></Suspense>} />
      <Route path="/verificacion/:codigo" element={<Suspense fallback={PageFallback}><VerificacionPublicPage /></Suspense>} />

      {/* 404 route */}
      <Route
        path="*"
        element={
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
              <p className="text-gray-600 mb-4">Página no encontrada</p>
              <a href="/dashboard" className="text-primary-600 hover:text-primary-500">Volver al Dashboard</a>
            </div>
          </div>
        }
      />
    </Routes>
  );
}

export default App;
